/* Copyright © 2026 Xerox Corporation. All Rights Reserved. Copyright protection claimed includes all forms and matters of copyrightable material and information now allowed by statutory or judicial law or hereinafter granted, including without limitation, material generated from the software programs which are displayed on the screen such as icons, screen display looks, etc. */

import {
  Component,
  OnInit,
  OnDestroy,
  Output,
  EventEmitter,
} from '@angular/core';
import { formatDate } from '@angular/common';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { BasicAlertComponent } from '../alerts/basic-alert.component';
import { Subscription } from 'rxjs/internal/Subscription';
import { UserService } from '../../services/user.service';
import { AppsService } from '../../services/apps.service';
import { AccessListService } from '../../services/access-list.service';
import { XdaStringsService } from '../../services/xda-strings.service';

@Component({
  selector: 'credit-usage-modal-component',
  templateUrl: './credit-usage-modal.component.html',
  styleUrls: ['./credit-usage-modal.component.less'],
  standalone: false,
})
export class CreditUsageModalComponent implements OnInit, OnDestroy {
  loading = true;
  refreshDisabled = false;
  accessListUsers: any;
  getUsage: Subscription;
  subscriptionTotal = 0;
  weeklyTotal = 0;
  monthlyTotal = 0;
  displayTotal = 0;
  subscriptionMax = 0;
  weeklyMax = 0;
  monthlyMax = 0;

  dateRange = [
    {
      rangeName: 'Last7Days',
      value: 'weeklyTotal',
      SDE_KEY: 'SDE_LAST_FMTSTR_DAYS1',
      order: 1,
    },
    {
      rangeName: 'LastMonth',
      value: 'monthlyTotal',
      SDE_KEY: 'SDE_LAST_MONTH4',
      order: 2,
    },
    {
      rangeName: 'ActiveSubscription',
      value: 'subscriptionTotal',
      SDE_KEY: 'SDE_CURRENTLY_ACTIVE_SUBSCRIPTION',
      order: 3,
    },
  ];

  selectedDateRange = this.dateRange[0];

  @Output() dataChange = new EventEmitter<void>();

  constructor(
    public activeModal: NgbActiveModal,
    private modalService: NgbModal,
    private userService: UserService,
    private appsService: AppsService,
    private accessListService: AccessListService,
    private xdaStringsService: XdaStringsService,
  ) {
    this.dateRange[0].SDE_KEY = this.getWeeklyLabel();
  }

  ngOnInit() {
    this.getAccessListUsersUsage();
  }

  refresh() {
    this.refreshDisabled = true;
    this.subscriptionTotal = 0;
    this.weeklyTotal = 0;
    this.monthlyTotal = 0;
    this.getAccessListUsersUsage();
    this.selectedDateRange = this.dateRange[0];
  }

  getAccessListUsersUsage() {
    if (!this.userService.user) {
      return;
    }

    this.getUsage = this.accessListService
      .getAccessListUsersUsage(this.userService.user.accessListId, true)
      .subscribe(
        (json) => {
          this.accessListUsers = JSON.parse(json.toString());
          this.accessListUsers.forEach((u: any) => {
            if (!u.usage) {
              u.usage = {
                subscriptionTotal: 0,
                weeklyTotal: 0,
                monthlyTotal: 0,
              };
            }

            this.subscriptionTotal += parseInt(u.usage.subscriptionTotal);
            this.weeklyTotal += parseInt(u.usage.weeklyTotal);
            this.monthlyTotal += parseInt(u.usage.monthlyTotal);
            u.displayTotal = parseInt(u.usage.weeklyTotal); // Set weeklyTotal as the default
            if (this.weeklyMax < u.usage.weeklyTotal) {
              this.weeklyMax = u.usage.weeklyTotal;
            }
            if (this.monthlyMax < u.usage.monthlyTotal) {
              this.monthlyMax = u.usage.monthlyTotal;
            }
            if (this.subscriptionMax < u.usage.subscriptionTotal) {
              this.subscriptionMax = u.usage.subscriptionTotal;
            }
          });
          this.displayTotal = this.weeklyTotal; // Set weeklyTotal as the default
          this.accessListUsers.sort((a: any, b: any) => {
            return b.displayTotal - a.displayTotal;
          });
          this.accessListUsers.forEach((u: any) => {
            if (u.usage) {
              u.displayPct =
                this.weeklyMax === 0
                  ? 0
                  : Math.floor((u.displayTotal / this.weeklyMax) * 100);
            }
          });
          this.accessListService.emitRefreshSuccessEvent();

          this.refreshDisabled = false;
          this.loading = false;
        },
        (err) => {
          this.refreshDisabled = false;
          const modalRef = this.modalService.open(BasicAlertComponent);
          modalRef.componentInstance.title = 'SDE_USAGE_DATA_NOT';
          modalRef.componentInstance.body = 'SDE_PLEASE_TRY_AGAIN2';
          modalRef.result.then(() => {
            this.activeModal.dismiss();
          });
        },
      );
  }

  getWeeklyLabel() {
    return this.xdaStringsService.strings['SDE_LAST_FMTSTR_DAYS1'].replace(
      '{0}',
      '7',
    );
  }

  onDateRangeSelected() {
    switch (this.selectedDateRange.value) {
      case 'subscriptionTotal':
        this.accessListUsers.forEach((u: any) => {
          if (u.usage) {
            u.displayTotal = parseInt(u.usage.subscriptionTotal);
            u.displayPct =
              this.subscriptionMax === 0
                ? 0
                : Math.floor((u.displayTotal / this.subscriptionMax) * 100);
          }
        });
        this.displayTotal = this.subscriptionTotal;
        break;
      case 'monthlyTotal':
        this.accessListUsers.forEach((u: any) => {
          if (u.usage) {
            u.displayTotal = parseInt(u.usage.monthlyTotal);
            u.displayPct =
              this.monthlyMax === 0
                ? 0
                : Math.floor((u.displayTotal / this.monthlyMax) * 100);
          }
        });
        this.displayTotal = this.monthlyTotal;
        break;
      case 'weeklyTotal':
      default:
        this.accessListUsers.forEach((u: any) => {
          if (u.usage) {
            u.displayTotal = parseInt(u.usage.weeklyTotal);
            u.displayPct =
              this.weeklyMax === 0
                ? 0
                : Math.floor((u.displayTotal / this.weeklyMax) * 100);
          }
        });
        this.displayTotal = this.weeklyTotal;
        break;
    }
    this.accessListUsers.sort((a: any, b: any) => {
      return b.displayTotal - a.displayTotal;
    });
  }

  downloadCSV() {
    const datetimestr = formatDate(
      new Date().getTime(),
      ' yyyy-MM-dd hh.mm.ss aaa',
      'en-US',
    );
    const fileName =
      this.xdaStringsService.strings['SDE_CREDIT_USAGE'] +
      ' ' +
      datetimestr +
      '.csv';
    const csvString = this.toCSVString(this.accessListUsers);
    const element = document.createElement('a');

    //https://stackoverflow.com/questions/42462764/javascript-export-csv-encoding-utf-8-issue
    element.setAttribute(
      'href',
      'data:text/csv;charset=utf-8,' + encodeURIComponent('\ufeff' + csvString),
    );
    element.setAttribute('download', fileName);
    element.style.display = 'none';
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  }

  private toCSVString(arr: any): string {
    const coloumnHeader = [
      this.xdaStringsService.strings['SDE_NAME3'],
      this.xdaStringsService.strings['SDE_EMAIL2'],
      this.xdaStringsService.strings['SDE_LAST_FMTSTR_DAYS1'].replace(
        '{0}',
        '7',
      ),
      this.xdaStringsService.strings['SDE_LAST_MONTH4'],
      this.xdaStringsService.strings['SDE_CURRENTLY_ACTIVE_SUBSCRIPTION'],
    ];

    const arrForCSV = arr.map((item: any) => [
      '\"' + item.userLastName + ', ' + item.userFirstName + '\"',
      item.userEmail,
      parseInt(item.usage.weeklyTotal),
      parseInt(item.usage.monthlyTotal),
      parseInt(item.usage.subscriptionTotal),
    ]);

    arrForCSV.splice(0, 0, coloumnHeader);
    return arrForCSV.map((e: any) => e.join(',')).join('\n');
  }

  ngOnDestroy() {
    this.getUsage.unsubscribe();
  }
}
